/**
 * Company Service
 * Centralized service for all company-related operations
 */

import { supabase, handleSupabaseArray } from '@xanectech/supabase-client';
import { normalizeId, createLogger } from '@xanectech/lib';
import type {
  Company,
  CompanyUser,
  CompanyStats,
  CompanyUserStats,
  CompanyAnalyticsData,
} from '@xanectech/types';

const logger = createLogger('Data - CompanyService');

interface CompanySubscriptionRow {
  is_active: boolean | null;
  inactive_at: string | null;
}

interface CompanySummaryRow {
  id: string;
  company_code: string;
  name: string;
  email?: string | null;
  website?: string | null;
  description?: string | null;
  logo_url?: string | null;
  icon_url?: string | null;
  receive_field_change_notifications?: boolean | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  subscriptions?: CompanySubscriptionRow[] | null;
  user_profiles?: Array<{ count: number | null }> | null;
}

interface UserProfileAnalyticsRow {
  user_id: string;
  is_active: boolean | null;
}

interface TwoFAAnalyticsRow {
  enabled: boolean | null;
  user_id: string;
}

interface SharingActivity {
  user_id: string;
  company_id: string;
  created_at: string;
  status: string | null;
}

export class CompanyService {
  /**
   * Search companies by name or company code (active only)
   */
  static async searchCompanies(
    query: string,
    limit: number = 15,
  ): Promise<Company[]> {
    const { data, error } = await supabase
      .from('companies')
      .select(
        `id,
        company_code,
        name,
        email,
        website,
        description,
        logo_url,
        icon_url,
        is_active,
        created_at,
        updated_at,
        subscriptions (
          is_active
        )`,
      )
      .eq('is_active', true)
      .or(`name.ilike.%${query}%,company_code.ilike.%${query}%`)
      .order('name')
      .limit(limit);

    if (error) {
      logger.error('Error searching companies:', { error });
      throw error;
    }

    return (
      data?.map((company) => ({
        ...company,
        email: company.email || undefined,
        website: company.website || undefined,
        description: company.description || undefined,
        logo_url: company.logo_url || undefined,
        icon_url: company.icon_url || undefined,
        subscription_status: company.subscriptions?.[0]?.is_active ?? false,
      })) || []
    );
  }

  /**
   * Get all companies (for system admins)
   */
  static async getAllCompanies(hasPermission: boolean): Promise<Company[]> {
    const { data, error } = hasPermission
      ? await supabase.from('companies').select(
          `*, 
            user_roles:user_roles(count), 
            subscriptions (
            is_active,
            inactive_at
            )`,
        )
      : await supabase.from('companies').select(
          `id,
            company_code,
            name,
            website,
            description,
            logo_url,
            receive_field_change_notifications,
            created_at,
            updated_at,
            is_active,
            user_roles:user_roles(count),
            subscriptions (
            is_active,
            inactive_at)`,
        );

    if (error) {
      logger.error(
        'Error fetching companies:',
        { error },
        'CompanyService.getAllCompanies',
      );
      throw error;
    }

    logger.info(`Fetched ${data?.length || 0} companies`, {
      category: 'CompanyService.getAllCompanies',
      data: data,
    });

    const companies = handleSupabaseArray<CompanySummaryRow>(data);

    return companies.map((company) => {
      const subscription = company.subscriptions?.[0];

      return {
        ...company,
        email: company.email ?? undefined,
        website: company.website || undefined,
        description: company.description || undefined,
        logo_url: company.logo_url || undefined,
        icon_url: company.icon_url || undefined,
        created_at: company.created_at ?? '',
        updated_at: company.updated_at ?? '',
        is_active: company.is_active ?? undefined,
        receive_field_change_notifications:
          company.receive_field_change_notifications || false,
        user_count: company.user_profiles?.[0]?.count || 0,
        admin_count: 0,
        subscription_status: subscription?.is_active ?? false,
        inactive_at: subscription?.inactive_at ?? undefined,
        last_activity: company.updated_at ?? undefined,
      };
    });
  }

  /**
   * Get company statistics
   */
  static async getCompanyStats(): Promise<CompanyStats> {
    // Get total companies
    const { count: totalCompanies, error: companiesError } = await supabase
      .from('companies')
      .select('*', { count: 'exact', head: true });

    if (companiesError) throw companiesError;

    // Get total users
    const { count: totalUsers, error: usersError } = await supabase
      .from('user_profiles')
      .select('*', { count: 'exact', head: true });

    if (usersError) throw usersError;

    // Get active subscriptions
    const { count: activeSubscriptions, error: subscriptionsError } =
      await supabase
        .from('subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

    if (subscriptionsError) throw subscriptionsError;

    return {
      totalCompanies: totalCompanies || 0,
      activeCompanies: totalCompanies || 0,
      totalUsers: totalUsers || 0,
      activeSubscriptions: activeSubscriptions || 0,
    };
  }

  /**
   * Get single company by ID
   */
  static async getCompany(
    companyId: string,
    hasPermission: boolean,
  ): Promise<Company | null> {
    const { data, error } = hasPermission
      ? await supabase
          .from('companies')
          .select(
            `*,
            subscriptions (
              is_active,
              inactive_at
            ) `,
          )
          .eq('id', companyId)
          .maybeSingle()
      : await supabase
          .from('companies')
          .select(
            `id,
            company_code, 
            name,
            email,
            website,
            description,
            logo_url,
            receive_field_change_notifications,
            created_at,
            updated_at,
            is_active,
            terms_and_conditions,
            subscriptions (
              is_active,
              inactive_at
            )
            `,
          )
          .eq('id', companyId)
          .single();

    if (!error && data) {
      const subscription = data.subscriptions?.[0];

      return {
        ...data,
        email: data.email ?? undefined,
        website: data.website ?? undefined,
        description: data.description ?? undefined,
        logo_url: data.logo_url ?? undefined,
        subscription_status: subscription?.is_active ?? false,
        inactive_at: subscription?.inactive_at ?? undefined,
      } as Company;
    }

    if (error) {
      logger.error('Error fetching company:', error);
      throw error;
    }

    return null;
  }

  /**
   * Create new company
   */
  static async createCompany(companyData: {
    company_code: string; // External system identifier (text)
    name: string;
    email: string;
    website?: string;
    description?: string;
    is_active?: boolean;
    logo_url?: string;
  }): Promise<Company> {
    // Get current user ID to set as primary admin
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('Authentication required to create company');
    }

    const { data, error } = await supabase
      .from('companies')
      .insert({
        name: companyData.name,
        email: companyData.email,
        website: companyData.website,
        description: companyData.description,
        logo_url: companyData.logo_url,
        is_active: companyData.is_active ?? true,
        company_code: companyData.company_code, // External system identifier
        primary_admin_id: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned when creating company');
    return data as Company;
  }

  /**
   * Update company
   */
  static async updateCompany(
    id: string,
    updates: Partial<Company>,
  ): Promise<Company> {
    const formattedId = normalizeId(id);
    const isUuidLike =
      /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(
        formattedId as string,
      );

    // Try to update by UUID first if it looks like one
    if (isUuidLike) {
      const { data, error } = await supabase
        .from('companies')
        .update(updates)
        .eq('id', formattedId)
        .select()
        .maybeSingle();

      if (!error && data) {
        return data as Company;
      }
    }

    // If not found by UUID, try by company_code
    const { data, error } = await supabase
      .from('companies')
      .update(updates)
      .eq('company_code', id)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned when updating company');
    return data as Company;
  }

  /**
   * Delete company
   */
  static async deleteCompany(id: string): Promise<void> {
    const { error } = await supabase.from('companies').delete().eq('id', id);
    if (error) throw error;
  }

  /**
   * Upload company logo or icon
   */
  static async uploadCompanyAsset(
    id: string,
    file: File,
    assetType: 'logo' | 'icon',
  ): Promise<string> {
    // Validate file type
    const validTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/svg+xml',
    ];
    if (!validTypes.includes(file.type)) {
      throw new Error(
        'Invalid file type. Please upload a JPEG, PNG, WEBP, or SVG image.',
      );
    }

    // Validate file size (5MB)
    if (file.size > 5242880) {
      throw new Error('File size must be less than 5MB.');
    }

    // Create file path using company ID for RLS compatibility
    const fileExt = file.name.includes('.')
      ? file.name.split('.').pop()
      : file.type.split('/').pop() || 'png';
    const uniqueId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : Date.now().toString();
    const fileName = `${assetType}-${uniqueId}.${fileExt}`;
    const filePath = `${normalizeId(id)}/${fileName}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage
      .from('company-assets')
      .upload(filePath, file, {
        upsert: true,
        contentType: file.type,
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from('company-assets').getPublicUrl(filePath);

    // Update company record with new URL
    const updateField = assetType === 'logo' ? 'logo_url' : 'icon_url';
    await this.updateCompany(id, {
      [updateField]: publicUrl,
    } as Partial<Company>);

    return publicUrl;
  }

  /**
   * Delete company asset (logo or icon)
   */
  static async deleteCompanyAsset(
    companyId: string,
    assetUrl: string,
    assetType: 'logo' | 'icon',
  ): Promise<void> {
    // Extract file path from URL
    const urlParts = assetUrl.split('/company-assets/');
    if (urlParts.length < 2 || !urlParts[1]) return;

    const filePath = urlParts[1];

    // Delete file from storage
    const { error: deleteError } = await supabase.storage
      .from('company-assets')
      .remove([filePath]);

    if (deleteError) throw deleteError;

    // Update company record to remove URL
    const updateField = assetType === 'logo' ? 'logo_url' : 'icon_url';
    await this.updateCompany(companyId, {
      [updateField]: null,
    } as Partial<Company>);
  }

  /**
   * Get company users
   */
  static async getCompanyUsers(companyId: string): Promise<CompanyUser[]> {
    // companyId is now expected to be a UUID that references companies.id
    const { data, error } = await supabase
      .from('user_profiles')
      .select(
        `
        *,
        user_roles:user_roles(role, is_active)
      `,
      )
      .eq('company_id', companyId);

    if (error) throw error;

    // Transform data to include role information and real email
    const users = await Promise.all(
      (data || []).map(async (user) => {
        const userRole =
          Array.isArray(user.user_roles) && user.user_roles.length > 0
            ? (user.user_roles[0]?.role ?? user.role ?? 'end_user')
            : user.role || 'end_user';

        // Get real email from auth.users table
        try {
          const { data: emailData, error: _emailError } = await supabase.rpc(
            'get_user_email',
            { _user_id: user.user_id },
          );

          return {
            ...user,
            role: userRole,
            email: emailData || 'Email unavailable',
            name:
              user.display_name ||
              `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            department: 'General',
            status: user.is_active ? 'active' : 'inactive',
            joinDate: user.created_at,
          };
        } catch (error) {
          logger.error('Failed to get email for user:', {
            userid: user.user_id,
            error: error instanceof Error ? error.message : String(error),
          });
          return {
            ...user,
            role: userRole,
            email: 'Email unavailable',
            name:
              user.display_name ||
              `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            department: 'General',
            status: user.is_active ? 'active' : 'inactive',
            joinDate: user.created_at,
          };
        }
      }),
    );

    return users as CompanyUser[];
  }

  /**
   * Get company user statistics
   */
  static async getCompanyUserStats(
    companyId: string,
  ): Promise<CompanyUserStats> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('company_id', companyId);

    if (error) throw error;

    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      totalUsers: data?.length || 0,
      activeUsers: data?.filter((user) => user.is_active).length || 0,
      // departments: 3,
      newThisMonth:
        data?.filter((user) => {
          const createdAt = user.created_at;
          if (typeof createdAt === 'string') {
            return new Date(createdAt) >= thisMonth;
          }
          return false;
        }).length || 0,
    };
  }

  /**
   * Get current user's company ID (returns UUID)
   */
  static async getCurrentUserCompany(userId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('company_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data?.company_id || null;
  }

  /**
   * Helper method to convert company external code to UUID
   * For external system integration - converts company_code to companies.id
   */
  static async getCompanyIdFromCode(
    companyCode: string,
  ): Promise<string | null> {
    const { data, error } = await supabase
      .from('companies')
      .select('id')
      .eq('company_code', companyCode)
      .maybeSingle();

    if (error) throw error;
    return data?.id || null;
  }

  /**
   * Helper method to convert company UUID to external code
   * For external system integration - converts companies.id to company_code
   */
  static async getCompanyCodeFromId(companyId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('companies')
      .select('company_code')
      .eq('id', companyId)
      .maybeSingle();

    if (error) throw error;
    return data?.company_code || null;
  }

  /**
   * Get company analytics
   */
  static async getCompanyAnalytics(
    companyId: string,
  ): Promise<CompanyAnalyticsData> {
    // Fetch users for the company
    const { data: users, error: usersError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('company_id', companyId);

    if (usersError) throw usersError;

    // Fetch data sharing responses for analytics
    // DEPRECATED: data_sharing_responses table has been replaced by contract_submissions
    // Use contract_submissions table instead for new implementations
    const sharingData: SharingActivity[] = [];

    const usersList = handleSupabaseArray<UserProfileAnalyticsRow>(users);

    // Fetch 2FA data for security metrics
    const { data: twoFAData, error: twoFAError } = await supabase
      .from('user_2fa')
      .select('enabled, user_id')
      .in(
        'user_id',
        usersList.map((user) => user.user_id),
      );

    if (twoFAError) throw twoFAError;

    // Calculate analytics
    const twoFAList = handleSupabaseArray<TwoFAAnalyticsRow>(twoFAData);

    const totalUsers = usersList.length;
    const activeUsers = usersList.filter((user) => user.is_active).length;
    const twoFAEnabled = twoFAList.filter((tfa) => tfa.enabled).length;
    const twoFAPercentage =
      totalUsers > 0 ? Math.round((twoFAEnabled / totalUsers) * 100) : 0;

    // Generate monthly data
    const userActivity = [
      {
        month: 'Jan',
        activeUsers: Math.max(1, activeUsers - 5),
        dataShares: sharingData?.length || 0,
      },
      {
        month: 'Feb',
        activeUsers: Math.max(1, activeUsers - 3),
        dataShares: (sharingData?.length || 0) + 5,
      },
      {
        month: 'Mar',
        activeUsers: Math.max(1, activeUsers - 1),
        dataShares: (sharingData?.length || 0) + 10,
      },
      {
        month: 'Apr',
        activeUsers: activeUsers,
        dataShares: (sharingData?.length || 0) + 15,
      },
      {
        month: 'May',
        activeUsers: activeUsers + 1,
        dataShares: (sharingData?.length || 0) + 20,
      },
      {
        month: 'Jun',
        activeUsers: activeUsers + 2,
        dataShares: (sharingData?.length || 0) + 25,
      },
    ];

    // Calculate department distribution
    const departments = ['Engineering', 'Marketing', 'Sales', 'HR', 'Finance'];
    const departmentUsage = departments.map((dept, index) => {
      const userCount = Math.max(
        1,
        Math.floor(totalUsers / departments.length) + (index % 2),
      );
      return {
        department: dept,
        users: userCount,
        percentage: Math.round((userCount / totalUsers) * 100) || 0,
      };
    });

    // Security metrics
    const securityMetrics = {
      twoFactorEnabled: twoFAPercentage,
      passwordStrength: 85,
      dataEncryption: 100,
      accessControl: 88,
    };

    // Recent activities
    const recentActivities = [];
    for (const activity of sharingData?.slice(0, 4) || []) {
      const { data: userData } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('user_id', activity.user_id)
        .single();

      recentActivities.push({
        action: 'Data Share Request',
        user: userData
          ? `${userData.first_name || 'User'} ${
              userData.last_name || ''
            }`.trim()
          : 'Unknown User',
        company: activity.company_id,
        time: this.getTimeAgo(String(activity.created_at)),
        status: activity.status || 'pending',
      });
    }

    // Key metrics
    const keyMetrics = {
      monthlyActiveUsers: activeUsers,
      dataShares: sharingData?.length || 0,
      securityScore: Math.round(
        (securityMetrics.twoFactorEnabled +
          securityMetrics.passwordStrength +
          securityMetrics.dataEncryption +
          securityMetrics.accessControl) /
          4,
      ),
      avgResponseTime: '2.4h',
    };

    return {
      userActivity,
      departmentUsage,
      securityMetrics,
      recentActivities,
      keyMetrics,
    };
  }

  /**
   * Invite user to company (legacy - direct DB insert without auth)
   */
  static async inviteUser(userData: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    company_id: string;
  }): Promise<CompanyUser> {
    const { data, error } = await supabase
      .from('user_profiles')
      .insert({
        user_id: crypto.randomUUID(),
        first_name: userData.first_name,
        last_name: userData.last_name,
        company_id: userData.company_id,
        role: userData.role,
        is_active: false,
      })
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned when inviting user');
    return data as CompanyUser;
  }

  /**
   * Create a company user via edge function (sends set-password email, no password required)
   */
  static async createCompanyUser(userData: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    company_id: string;
    department?: string;
  }): Promise<CompanyUser> {
    const invokeResult = await supabase.functions.invoke(
      'create-company-user',
      {
        body: userData,
      },
    );

    if (invokeResult.error) throw invokeResult.error;

    const result = invokeResult.data as {
      success: boolean;
      user_id: string;
      error?: string;
    };
    if (!result.success) {
      throw new Error(result.error ?? 'Failed to create company user');
    }

    return {
      id: result.user_id,
      user_id: result.user_id,
      email: userData.email,
      first_name: userData.first_name,
      last_name: userData.last_name,
      role: userData.role,
      company_id: userData.company_id,
      is_active: true,
    } as CompanyUser;
  }

  /**
   * Update company user
   */
  static async updateCompanyUser(
    userId: string,
    companyId: string,
    updates: Partial<CompanyUser>,
  ): Promise<CompanyUser> {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('user_id', userId)
      .eq('company_id', companyId)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned when updating company user');
    return data as CompanyUser;
  }

  /**
   * Toggle user status
   */
  static async toggleUserStatus(
    userId: string,
    companyId: string,
    isActive: boolean,
  ): Promise<CompanyUser> {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ is_active: isActive })
      .eq('user_id', userId)
      .eq('company_id', companyId)
      .select()
      .single();

    if (error) throw error;
    if (!data) throw new Error('No data returned when toggling user status');
    return data as CompanyUser;
  }

  /**
   * Toggle company subscription status
   */
  static async toggleCompanySubscription(
    companyId: string,
    isActive: boolean,
  ): Promise<Company> {
    const { data, error } = await supabase
      .from('subscriptions')
      .update({
        is_active: isActive,
        inactive_at: !isActive ? new Date().toISOString() : null,
      })
      .eq('company_id', companyId)
      .select()
      .single();

    if (error) throw error;

    // Fetch updated company data
    const updatedCompany = await this.getCompany(companyId, true);
    if (!updatedCompany) {
      throw new Error('Failed to fetch updated company data');
    }

    return updatedCompany;
  }

  /**
   * Helper method to get time ago
   */
  private static getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Less than an hour ago';
    }
  }
}
